import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Smartphone, Lock, Eye, ShieldCheck, ArrowRight } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const layers = [
  { icon: Smartphone, badge: "Step 1", title: "Patient Captures Context",
    bullets: ["Private symptom capture", "Mental health prompts", "Voice & multilingual", "Digital intake"],
    gradient: "linear-gradient(135deg, #00A8B3 0%, #00838F 50%, #006064 100%)" },
  { icon: Lock, badge: "Step 2", title: "Visit-Based OTP Consent",
    bullets: ["OTP-gated sharing", "Source-tagged access", "Admin-safe flows", "DPDP compliant"],
    gradient: "linear-gradient(135deg, #28A745 0%, #1B8A36 50%, #0D6B24 100%)" },
  { icon: Eye, badge: "Step 3", title: "5-Second Doctor Timeline",
    bullets: ["Complete timeline in 5 sec", "Read-only AI summaries", "Non-diagnostic", "Instant context"],
    gradient: "linear-gradient(135deg, #00A8B3 0%, #0097A7 50%, #00796B 100%)" },
];

export const ArchitectureSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="architecture" className="py-20 lg:py-28" style={{ background: '#F0F8FF' }} ref={ref}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-12">
          <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}}
            className="text-spaced mb-3 block">H O W &nbsp; I T &nbsp; W O R K S</motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4" style={{ color: '#1A1F36' }}>Our 3-Step Process</h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: '#6B7280' }}>A privacy-first architecture serving patients, doctors, and hospitals.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
          {/* Connecting arrows (desktop) */}
          {[1, 2].map(idx => (
            <div key={idx} className="hidden lg:flex absolute top-1/2 z-10" style={{ left: `${idx * 33.3}%`, transform: 'translate(-50%, -50%)' }}>
              <motion.div animate={{ x: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: idx * 0.3 }}
                className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'white' }}>
                <ArrowRight className="w-5 h-5" style={{ color: '#00A8B3' }} />
              </motion.div>
            </div>
          ))}

          {layers.map((l, i) => (
            <motion.div key={l.badge}
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: i * 0.2 + 0.3, duration: 0.6, type: "spring" }}
              whileHover={{ y: -6, scale: 1.02 }}
            >
              <div className="rounded-2xl p-7 h-full flex flex-col text-white shadow-lg relative overflow-hidden"
                style={{ background: l.gradient }}>
                {/* Animated shimmer overlay */}
                <motion.div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)' }}
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 + 1 }} />

                {/* Decorative circles */}
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
                <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full" style={{ background: 'rgba(255,255,255,0.04)' }} />

                <div className="flex items-center gap-3 mb-5 relative z-10">
                  <motion.div
                    animate={inView ? { rotate: [0, 360] } : {}}
                    transition={{ delay: i * 0.3 + 1, duration: 20, repeat: Infinity, ease: "linear" }}
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                    <l.icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <motion.span initial={{ opacity: 0, x: -10 }} animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: i * 0.2 + 0.5 }}
                    className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    {l.badge}
                  </motion.span>
                </div>

                <motion.h3 initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.2 + 0.6 }}
                  className="text-lg font-heading font-bold text-white mb-4 relative z-10">{l.title}</motion.h3>

                <ul className="space-y-2.5 mt-auto relative z-10">
                  {l.bullets.map((b, j) => (
                    <motion.li key={b}
                      initial={{ opacity: 0, x: -15 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: i * 0.2 + j * 0.1 + 0.7 }}
                      className="flex items-start gap-2 text-sm">
                      <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
                      <span style={{ color: 'rgba(255,255,255,0.85)' }}>{b}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
